import React from "react";
import PathConstants from "./pathConstants";
import {
  RedirectHomeRoute,
  ProtectedRoute,
  FeatureFlagProtectedRoute,
} from "./index";
import { logger } from "../utils/LogUtils";
import { FeatureFlag } from "../utils/featureFlags";
import Box from "@amzn/awsui-components-console/box";
import Spinner from "@amzn/awsui-components-console/spinner";

const handleImportError = (error: Error) => {
  logger.error("Failed to load component reloading page!", {
    error: error.toString(),
  });
  window.location.reload();
  return {
    default: () => (
      <Box textAlign="center" padding="xxl">
        <Spinner />
      </Box>
    ),
  };
};

const Dashboard = React.lazy(() =>
  import("../pages/dashboard/Dashboard").catch((error) =>
    handleImportError(error)
  )
);
const Help = React.lazy(() =>
  import("../pages/core/Help").catch((error) => handleImportError(error))
);

// Releases
const Releases = React.lazy(() =>
  import("../pages/releases/Releases").catch((error) =>
    handleImportError(error)
  )
);
const ReleaseForm = React.lazy(() =>
  import("../pages/releases/ReleaseForm").catch((error) =>
    handleImportError(error)
  )
);
const DemoCreation = React.lazy(() =>
  import("../pages/democreation/DemoCreation").catch((error) =>
    handleImportError(error)
  )
);

// Roadmap
const Roadmap = React.lazy(() =>
  import("../pages/roadmap/Roadmap").catch((error) => handleImportError(error))
);
const RoadmapForm = React.lazy(() =>
  import("../pages/roadmap/RoadmapForm").catch((error) =>
    handleImportError(error)
  )
);

const Login = React.lazy(() =>
  import("../pages/core/Login").catch((error) => handleImportError(error))
);

const Chat = React.lazy(() =>
  import("../pages/chat/Chat").catch((error) => handleImportError(error))
);

const ChatList = React.lazy(() =>
  import("../pages/chat/ChatList").catch((error) => handleImportError(error))
);

// Demo
const DemoList = React.lazy(() =>
  import("../pages/demos/DemoList").catch((error) => handleImportError(error))
);
const DemoFavouritesList = React.lazy(() =>
  import("../pages/demos/DemoFavouritesList").catch((error) =>
    handleImportError(error)
  )
);
const DemoSearchList = React.lazy(() =>
  import("../pages/demos/DemoSearchList").catch((error) =>
    handleImportError(error)
  )
);
const DemoPlayHistory = React.lazy(() =>
  import("../pages/demos/DemoPlayHistory").catch((error) =>
    handleImportError(error)
  )
);
const AdminDemoAttribution = React.lazy(() =>
  import("../pages/demos/AdminDemoAttribution").catch((error) =>
    handleImportError(error)
  )
);
const DemoDetails = React.lazy(() =>
  import("../pages/demos/DemoDetails").catch((error) =>
    handleImportError(error)
  )
);
const DemoForm = React.lazy(() =>
  import("../pages/demos/DemoForm").catch((error) => handleImportError(error))
);
const DemoTypeSelection = React.lazy(() =>
  import("../pages/demos/DemoTypeSelection").catch((error) =>
    handleImportError(error)
  )
);
const DemoUpload = React.lazy(() =>
  import("../pages/demos/DemoUpload").catch((error) => handleImportError(error))
);
const DemoProjectBoard = React.lazy(() =>
  import("../pages/demos/DemoProjectBoard").catch((error) =>
    handleImportError(error)
  )
);

// Variant
const VariantForm = React.lazy(() =>
  import("../pages/variant/VariantForm").catch((error) =>
    handleImportError(error)
  )
);

const VariantVersionList = React.lazy(() =>
  import("../pages/variant/VariantVersionList").catch((error) =>
    handleImportError(error)
  )
);

// Feature
const FeatureForm = React.lazy(() =>
  import("../pages/feature/FeatureForm").catch((error) =>
    handleImportError(error)
  )
);
const FeatureList = React.lazy(() =>
  import("../pages/feature/FeatureList").catch((error) =>
    handleImportError(error)
  )
);

// Feedback
const FeedbackForm = React.lazy(() =>
  import("../pages/feedback/FeedbackForm").catch((error) =>
    handleImportError(error)
  )
);
const FeedbackList = React.lazy(() =>
  import("../pages/feedback/Feedback").catch((error) =>
    handleImportError(error)
  )
);
const FeedbackConfigure = React.lazy(() =>
  import("../pages/feedback/FeedbackConfigure").catch((error) =>
    handleImportError(error)
  )
);

// Users
const UserList = React.lazy(() =>
  import("../pages/user/UserList").catch((error) => handleImportError(error))
);

const UserCreate = React.lazy(() =>
  import("../pages/user/UserCreate").catch((error) => handleImportError(error))
);

const UserImport = React.lazy(() =>
  import("../pages/user/UserImport").catch((error) => handleImportError(error))
);

const UserConfigure = React.lazy(() =>
  import("../pages/user/UserConfigure").catch((error) =>
    handleImportError(error)
  )
);

const PartnerDomainList = React.lazy(() =>
  import("../pages/partner/PartnerDomainList").catch((error) =>
    handleImportError(error)
  )
);

const PartnerUserApprovalList = React.lazy(() =>
  import("../pages/partner/PartnerUserApprovalList").catch((error) =>
    handleImportError(error)
  )
);

const ManageStaticContentOptions = React.lazy(() =>
  import("../pages/admin/ManageStaticContentOptions").catch((error) =>
    handleImportError(error)
  )
);

/**
 * Valid routes
 */
export const routes = [
  {
    path: PathConstants.LOGIN,
    element: (
      <RedirectHomeRoute>
        <Login />
      </RedirectHomeRoute>
    ),
  },
  {
    path: PathConstants.DASHBOARD,
    element: (
      <ProtectedRoute notAPartnerRequired={true}>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.CHAT_NEW,
    element: (
      <ProtectedRoute notAPartnerRequired={true}>
        <Chat />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.CHAT,
    element: (
      <ProtectedRoute notAPartnerRequired={true}>
        <Chat />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.CHAT_LIST,
    element: (
      <ProtectedRoute notAPartnerRequired={true}>
        <ChatList />
      </ProtectedRoute>
    ),
  },
  // Release notes
  {
    path: PathConstants.RELEASES,
    element: (
      <ProtectedRoute>
        <Releases />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.RELEASES_CREATE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <ReleaseForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.RELEASES_CONFIGURE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <ReleaseForm />
      </ProtectedRoute>
    ),
  },
  // Roadmap items
  {
    path: PathConstants.ROADMAP,
    element: (
      <ProtectedRoute>
        <Roadmap />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.ROADMAP_CREATE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <RoadmapForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.ROADMAP_CONFIGURE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <RoadmapForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.HELP,
    element: (
      <ProtectedRoute notAPartnerRequired={true}>
        <Help />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_CREATION,
    element: (
      <ProtectedRoute notAPartnerRequired={true}>
        <DemoCreation />
      </ProtectedRoute>
    ),
  },
  // Demo
  {
    path: PathConstants.DEMO_FAVOURITES_LIST,
    element: (
      <ProtectedRoute>
        <DemoFavouritesList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_SEARCH_LIST,
    element: (
      <ProtectedRoute>
        <DemoSearchList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_PLAY_HISTORY,
    element: (
      <FeatureFlagProtectedRoute featureFlag={FeatureFlag.DEMO_HISTORY_PLAYS}>
        <DemoPlayHistory />
      </FeatureFlagProtectedRoute>
    ),
  },
  {
    path: PathConstants.ADMIN_DEMO_ATTRIBUTION,
    element: (
      <FeatureFlagProtectedRoute
        featureFlag={FeatureFlag.DEMO_HISTORY_PLAYS}
        adminRequired={true}
      >
        <AdminDemoAttribution />
      </FeatureFlagProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_LIST,
    element: (
      <ProtectedRoute>
        <DemoList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_PROJECT_BOARD,
    element: (
      <ProtectedRoute adminRequired={true}>
        <DemoProjectBoard />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_CONFIGURE,
    element: (
      <ProtectedRoute builderRequired={true}>
        <DemoForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_TYPE_SELECT,
    element: (
      <ProtectedRoute adminRequired={true}>
        <DemoTypeSelection />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_CREATE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <DemoForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_DETAILS,
    element: (
      <ProtectedRoute>
        <DemoDetails />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.DEMO_UPLOAD,
    element: (
      <ProtectedRoute builderRequired={true}>
        <DemoUpload />
      </ProtectedRoute>
    ),
  },
  // Variant
  {
    path: PathConstants.VARIANT_CREATE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <VariantForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.VARIANT_CONFIGURE,
    element: (
      <ProtectedRoute builderRequired={true}>
        <VariantForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.VARIANT_VERSIONS_LIST,
    element: (
      <ProtectedRoute builderRequired={true}>
        <VariantVersionList />
      </ProtectedRoute>
    ),
  },
  // Feature
  {
    path: PathConstants.FEATURE_CREATE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <FeatureForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.FEATURE_CONFIGURE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <FeatureForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.FEATURE_LIST,
    element: (
      <ProtectedRoute adminRequired={true}>
        <FeatureList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.FEEDBACK,
    element: (
      <ProtectedRoute>
        <FeedbackForm />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.FEEDBACK_LIST,
    element: (
      <ProtectedRoute adminRequired={true}>
        <FeedbackList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.FEEDBACK_CONFIGURE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <FeedbackConfigure />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.USER_LIST,
    element: (
      <ProtectedRoute partnerAdminRequired={true}>
        <UserList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.USER_CREATE,
    element: (
      <ProtectedRoute partnerAdminRequired={true}>
        <UserCreate />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.USER_IMPORT,
    element: (
      <ProtectedRoute partnerAdminRequired={true}>
        <UserImport />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.USER_CONFIGURE,
    element: (
      <ProtectedRoute adminRequired={true}>
        <UserConfigure />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.PARTNER_DOMAIN_LIST,
    element: (
      <ProtectedRoute partnerAdminRequired={true}>
        <PartnerDomainList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.PARTNER_APPROVAL_LIST,
    element: (
      <ProtectedRoute partnerAdminRequired={true}>
        <PartnerUserApprovalList />
      </ProtectedRoute>
    ),
  },
  {
    path: PathConstants.ADMIN_DEMO_FORM_OPTIONS,
    element: (
      <ProtectedRoute adminRequired={true}>
        <ManageStaticContentOptions />
      </ProtectedRoute>
    ),
  },
];
